// frontend/src/ActivityLog/ActivityLog.jsx — /activity: everyone's activity, filtered, newest first. Polaris throughout.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge, Banner, BlockStack, Box, Button, Card, IndexTable, InlineGrid, InlineStack, Page, Select, Spinner, Text, TextField } from '@shopify/polaris';
import { useDebounced } from '../ItAssets/common';
import { activityApi } from './apiClient';
import DateRangeFilter from './DateRangeFilter';

const KINDS = [{ value: '', label: 'All kinds' }, { value: 'action', label: 'Actions' }, { value: 'signin', label: 'Sign-ins' }, { value: 'signout', label: 'Sign-outs' }, { value: 'signin_failed', label: 'Failed sign-ins' }];
const KIND_LABEL = { action: 'Action', signin: 'Sign-in', signout: 'Sign-out', signin_failed: 'Failed sign-in' };
const FILTERS = ['q', 'user', 'tool', 'kind', 'since', 'until'];
const when = (iso) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit', timeZone: 'America/New_York' });

function kindBadge(it) {
  const tone = it.tone === 'bad' || it.kind === 'signin_failed' ? 'critical' : it.tone === 'ok' || it.kind === 'signin' ? 'success' : it.kind === 'action' ? 'info' : undefined;
  return <Badge tone={tone}>{KIND_LABEL[it.kind] || it.kind}</Badge>;
}

export default function ActivityLog() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => Object.fromEntries(FILTERS.map((k) => [k, params.get(k) || ''])), [params]);
  const [q, setQ] = useState(filters.q);
  const dq = useDebounced(q);
  const set = useCallback((patch) => setParams((prev) => { const next = new URLSearchParams(prev); Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); }); return next; }, { replace: true }), [setParams]);
  useEffect(() => { if (dq !== filters.q) set({ q: dq }); }, [dq]); // eslint-disable-line react-hooks/exhaustive-deps
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [choices, setChoices] = useState({ users: [], tools: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const load = useCallback(async (before) => {
    setLoading(true); setError('');
    try {
      const r = await activityApi.list({ ...filters, before, limit: 50 });
      setItems((prev) => (before ? [...prev, ...r.items] : r.items)); setHasMore(r.has_more); setChoices({ users: r.users, tools: r.tools });
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [filters]);
  useEffect(() => { load(null); }, [load]);
  const active = FILTERS.filter((k) => filters[k]).length;
  const clear = () => { setQ(''); setParams({}, { replace: true }); };
  const exportCsv = async () => {
    try { const b = await activityApi.csv(filters); const url = URL.createObjectURL(b); const a = document.createElement('a'); a.href = url; a.download = 'activity.csv'; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 60000); } catch (e) { setError(e.message); }
  };
  const userOptions = [{ value: '', label: 'All users' }, ...choices.users.map((u) => ({ value: u.username, label: u.name }))];
  const toolOptions = [{ value: '', label: 'All modules' }, ...choices.tools.map((t) => ({ value: t.id, label: t.title }))];
  return (
    <Page title="Activity" subtitle="Everything done on Utility Hub: sign-ins and every action in every module, newest first. Kept for 12 months." fullWidth
      primaryAction={{ content: 'Export CSV', onAction: exportCsv, disabled: !items.length }}
      secondaryActions={active ? [{ content: 'Clear filters', onAction: clear }] : []}>
      <BlockStack gap="400">
        <Card>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 3, lg: 5 }} gap="300">
            <TextField label="Search" value={q} onChange={setQ} placeholder="Search the action text" autoComplete="off" clearButton onClearButtonClick={() => setQ('')} />
            <Select label="User" options={userOptions} value={filters.user} onChange={(v) => set({ user: v })} />
            <Select label="Module" options={toolOptions} value={filters.tool} onChange={(v) => set({ tool: v })} />
            <Select label="Kind" options={KINDS} value={filters.kind} onChange={(v) => set({ kind: v })} />
            <DateRangeFilter label="Date" since={filters.since} until={filters.until} onChange={(range) => set(range)} />
          </InlineGrid>
        </Card>
        {error && <Banner tone="critical" onDismiss={() => setError('')}><p>{error}</p></Banner>}
        <Card padding="0">
          <IndexTable resourceName={{ singular: 'entry', plural: 'entries' }} itemCount={items.length} selectable={false}
            headings={[{ title: 'Time' }, { title: 'User' }, { title: 'Module' }, { title: 'Kind' }, { title: 'Action' }]}
            emptyState={<Box padding="800"><InlineStack align="center">{loading ? <Spinner accessibilityLabel="Loading activity" /> : <Text as="p" tone="subdued">{active ? 'Nothing matches these filters.' : 'No activity recorded yet.'}</Text>}</InlineStack></Box>}>
            {items.map((it, i) => (
              <IndexTable.Row id={String(it.id)} key={it.id} position={i}>
                <IndexTable.Cell><Text as="span" variant="bodyMd" numeric>{when(it.at)}</Text></IndexTable.Cell>
                <IndexTable.Cell>
                  <BlockStack gap="050">
                    <Text as="span" variant="bodyMd" fontWeight="medium">{it.user_name}</Text>
                    {it.user_name !== it.username && <Text as="span" variant="bodySm" tone="subdued">{it.username}</Text>}
                  </BlockStack>
                </IndexTable.Cell>
                <IndexTable.Cell><Text as="span">{it.tool_title}</Text></IndexTable.Cell>
                <IndexTable.Cell>{kindBadge(it)}</IndexTable.Cell>
                <IndexTable.Cell className="uh-wrap-cell"><Text as="span" breakWord>{it.text}</Text></IndexTable.Cell>
              </IndexTable.Row>
            ))}
          </IndexTable>
          {(hasMore || (loading && items.length > 0)) && (
            <Box padding="400" borderBlockStartWidth="025" borderColor="border">
              <InlineStack align="center"><Button onClick={() => load(items[items.length - 1].id)} loading={loading} disabled={loading}>Load more</Button></InlineStack>
            </Box>
          )}
        </Card>
      </BlockStack>
    </Page>
  );
}
